import React, { useEffect, useState } from "react";
import { IconButton, Chip, Spinner } from "components/ui";
import { Icon } from "components/hds";
import { Table } from "antd";
import styled from "styled-components/macro";
import { useTranslation } from "react-i18next";
import useFetch from "use-http";

import { Can, Modal } from "components";
import { useToaster } from "contexts/Toaster";
import { useModal } from "contexts/Modal";
import { useScenes } from "contexts/Scenes";
import { useCurrentProject } from "contexts/Projects";
import { useFormat } from "hooks/useDateFns";
import { PROJECTS, SCENES } from "common/routes.constants";
import { LinkOutlined } from "@ant-design/icons";
import TabHeader from "../../components/TabHeader";
import TabLayout from "../../components/TabLayout";
import CollapseStyle from "./components/Collapse.styles";
import CreateShareLinkForm from "./forms/CreateShareLinkForm";
import SharelinkMenu from "./components/SharelinkMenu";
import RenameShareLinkForm from "./forms/RenameShareLinkForm";
import copyToClipboard from "utils/copyToClipboard";

const Share = () => {
	const { t } = useTranslation();
	const toaster = useToaster();
	const modal = useModal();
	const formatDate = useFormat();
	const [
		showCreateShareLinkForm,
		setShowCreateShareLinkForm,
	] = useState<boolean>(false);
	const [
		showRenameShareLinkForm,
		setShowRenameShareLinkForm,
	] = useState<string>("");

	const sceneContext = useScenes();
	const currentScene = (sceneContext as any)?.current;
	const createShareLink = (sceneContext as any)?.createShareLink || (() => {});
	const currentProject = useCurrentProject();
	const [arrayOfTokens, setArrayOfTokens] = useState<any>();

	const toggleCreateShareLinkForm = () => {
		setShowCreateShareLinkForm(!showCreateShareLinkForm);
	};

	const { post, response } = useFetch();

	const columns = [
		{
			title: "Link Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "",
			key: "share",
			render: (_: any, record: any) => (
				<div
					style={{
						cursor: "pointer",
						color: "#1677ff",
						minWidth: "75px",
					}}
					onClick={() => {
						copyToClipboard(
							getPublicLink(record.value),
						);

						toaster.success({
							message: t("app.global.msg.title.success"),
							description: t("app.global.msg.copyToClipboard"),
						});
					}}
				>
					<LinkOutlined /> {String(t("app.global.tooltip.copyToClipboard"))}
				</div>
			),
		},
		{
			title: "Sessions",
			dataIndex: "nViews",
			key: "nViews",
		},
		{
			title: "Creation Date",
			dataIndex: "creation",
			key: "creation",
			render: (_: any, record: any) =>
				formatDate(new Date(record.creation), "dd.MM.yyyy"),
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			render: (_: any, record: any) => record.type || "editor",
		},
		{
			title: "ShareID",
			dataIndex: "pin",
			key: "pin",
			render: (_: any, record: any) => record.pin?.pin,
		},
		{
			key: "action",
			render: (_: any, record: any) => (
				<>
					<SharelinkMenu
						button={
							<div style={{ cursor: "pointer", width: "12px" }}>
								<Icon
									name="EllipsisVerticalIcon"
									size="15px"
									color="grey800"
								/>
							</div>
						}
						content={[
							{
								icon: "EmbedIcon",
								label: "app.global.general.embed",
								action: () => {
									copyToClipboard(
										`<iframe width="900px" height="500px" title="${
											record.name
										}" src="${getPublicLink(
											record.value,
										)}"></iframe>`,
									);

									toaster.success({
										message: t(
											"app.global.msg.title.success",
										),
										description: t(
											"app.global.msg.copyEmbedCodeToClipboard",
										),
									});
								},
							},
							{
								icon: "DeleteIcon",
								label: "app.global.btn.delete",
								action: () => {
									handleDeleteShareLink(record);
								},
							},
						]}
					/>
				</>
			),
		},
	];

	const deleteShareLink = async (item: any) => {
		if (!currentScene) {
			return;
		}

		await post(
			`/projects/${currentProject?._id}/scenes/${currentScene._id}/token/delete`,
			{
				tokens: [item.value],
			},
		);

		if (response.ok) {
			const filteredTokens = arrayOfTokens.filter(
				(token: any) => token.value !== item.value,
			);

			setArrayOfTokens(filteredTokens);

			toaster.success({
				message: t("app.global.msg.title.success"),
				description: t("msg.token.deleted"),
			});
		}
	};

	const handleDeleteShareLink = (item: any) => {
		modal.confirm({
			title: String(t("app.deleteToken.modal.title")),
			content: String(t("app.deleteToken.modal.content")),
			onOk: () => {
				deleteShareLink(item);
			},
		});
	};

	const baseUrl = React.useMemo(() => {
		const routePath = `/${PROJECTS}/${currentProject?.shortid}/${SCENES}/${currentScene?.shortid}`;
		// Build from origin + pathname, never location.href — href carries the
		// query string (e.g. ?startEditor=true) and hash into the share link.
		const path = `${window.location.origin}${window.location.pathname}`;
		return path.replace(routePath, "").replace(/\/+$/, "");
	}, [currentProject, currentScene]);

	const getPublicLink = (token: string) => `${baseUrl}/${SCENES}/${token}`;

	const handleRenameShareLink = async (tokenInfo: any) => {
		setShowRenameShareLinkForm("");
	};

	const handleCreateShareLink = async (tokenInfo: any) => {
		// Response of POST gonna contain the new token data (Share Link).
		const newShareLink: any = await createShareLink(tokenInfo);

		console.log("newShareLink", newShareLink);

		// Add created token into list of tokens (Share Links).
		if (currentScene && newShareLink) {
			const newArrayOfTokens = [
				newShareLink.newToken,
				...arrayOfTokens,
				// ...currentScene.tokens,
			];

			// Sort it by date.
			const sortedArrayOfTokens = newArrayOfTokens.sort(
				(a: any, b: any) => {
					const aDate = new Date(a.creation);
					const bDate = new Date(b.creation);
					// eslint-disable-next-line no-nested-ternary
					return aDate > bDate ? -1 : 1;
				},
			);

			setArrayOfTokens(sortedArrayOfTokens);
		}

		setShowCreateShareLinkForm(false);
	};

	useEffect(() => {
		if (currentScene) {
			setArrayOfTokens(currentScene.tokens);
		}
	}, [currentScene]);

	useEffect(() => {
		setShowCreateShareLinkForm(false);
	}, [currentScene]);

	return (
		<TabLayout {...({} as any)}>
			<TabHeader>
				<Can userRole={["senior"]} {...({} as any)}>
					<IconButton
						name="PlusIcon"
						size="mini"
						disabled={currentScene?.tokens === undefined} // enable only when scene/tokens are loaded
						onClick={toggleCreateShareLinkForm}
					/>
				</Can>
			</TabHeader>
			<div>
				<CollapseStyle />

				<Modal
					title={String(t("app.createToken.modal.title"))}
					open={showCreateShareLinkForm}
					onCancel={toggleCreateShareLinkForm}
					footer={false}
				>
					<CreateShareLinkForm
						handleCreateShareLink={handleCreateShareLink}
						visible={showCreateShareLinkForm}
					/>
				</Modal>

				<Modal
					title={String(t("app.global.btn.rename"))}
					open={!!showRenameShareLinkForm}
					onCancel={() => {
						setShowRenameShareLinkForm("");
					}}
					footer={false}
				>
					<RenameShareLinkForm
						handleRenameShareLink={handleRenameShareLink}
						shareLink={arrayOfTokens?.find(
							(token: any) =>
								token.value === showRenameShareLinkForm,
						)}
					/>
				</Modal>

				{currentScene?.tokens === undefined && <Spinner />}

				{!currentScene?.tokens?.length && arrayOfTokens?.length === 0 && (
					<StyledEmptyContainer>
						<Chip>
							{String(t("app.ItemToken.msg.noSceneTokenAvailable"))}
						</Chip>
					</StyledEmptyContainer>
				)}

				{(!!currentScene?.tokens?.length ||
					arrayOfTokens?.length > 0) && (
					<Table
						columns={columns}
						dataSource={arrayOfTokens}
						pagination={false}
						loading={!currentScene}
					/>
				)}
			</div>
		</TabLayout>
	);
};

const StyledEmptyContainer = styled.div`
	height: 100%;
	width: 100%;
	padding: 20px;
	padding-top: 0px;
	justify-content: center;
	align-items: center;
	display: flex;
`;
export default Share;
